import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, forwardRef, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'shared-switch',
  templateUrl: './switch.component.html',
  styleUrls: ['./switch.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SwitchComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [NgClass],
})
export class SwitchComponent implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() isDisabled = false;
  @Input() isAnimated = false;
  @Input() mode: 'switch' | 'toggle' = 'switch';
  @Input() activeColor: 'primary' | 'secondary' = 'primary';
  @Input() value: boolean;
  @Input({ required: true }) ariaLabel: string;
  @Output() valueChange = new EventEmitter<boolean>();

  get switchClasses(): Record<string, boolean> {
    return {
      animated: this.isAnimated,
      switch: this.mode === 'switch',
      'secondary-color': this.activeColor === 'secondary',
    };
  }

  // Set only when a form directive binds the switch; otherwise the parent owns the value through [value].
  private onChange: ((value: boolean) => void) | null = null;
  private onTouched: () => void = () => {};

  onClick(): void {
    if (this.isDisabled) {
      return;
    }

    const requested = !this.value;
    if (this.onChange) {
      this.value = requested;
      this.onChange(requested);
      this.onTouched();
    } else {
      this.valueChange.emit(requested);
    }
  }

  writeValue(value: boolean): void {
    this.value = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }
}
